import { SelectModified } from "components/SelectModified";
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { Title } from "../../components/Title";
import { AlertContainer } from "./styles"

interface AlertContextProps {
  openAlert: (type: boolean, caller?: string) => void;
  closeAlert: (caller?: string) => void;
  resetTypeContinue: () => void;
  setTextAlertInterface: (value:AlertTextsProps) => void;
  alert: AlertProps;
  caller?: string;
  selectValue: string;
}

interface AlertProviderProps {
  children: ReactNode;
}

type ColorBtnProps = "default" | "outline" | "blue" | "danger" | "warning" | "success";

interface AlertTextsProps {
  title?: string;
  description?: string;
  closeAlertText?: string;
  continueAlertText?: string;
  closeAlertTypeBtn?: ColorBtnProps;
  continueAlertTypeBtn?: ColorBtnProps;
  positionCenter?: boolean;
  selectOptions?: {value: string, label: string}[];
  actionNeeded?: boolean;
  reverseButtonsPosition?: boolean,
}

interface AlertProps {
  isVisible: boolean,
  load: boolean,
  type: boolean,
  typeContinue?: string,
}

const initAlert = {
  isVisible: false,
  load: false,
  type: false,
  typeContinue: "",
}

const initAlertInterface = {
  title: "",
  description: "",
  closeAlertText: "",
  continueAlertText: "",
  closeAlertTypeBtn: "outline" as ColorBtnProps,
  continueAlertTypeBtn: "blue" as ColorBtnProps,
  positionCenter: false,
  selectOptions: [],
  actionNeeded: false,
  reverseButtonsPosition: false,
}

const AlertContext = createContext({} as AlertContextProps);

export const AlertProvider = ({children}: AlertProviderProps) => {
  const [alert, setAlert] = useState<AlertProps>(initAlert);
  const [textAlertInterface, setTextAlertInterface] = useState<AlertTextsProps>(initAlertInterface);
  const [selectValue, setSelectValue] = useState<string>('');
  const [caller, setCaller] = useState<string>();
  
  const reverseObjectsButton = (isReverse: boolean) => {
    const left = {
      label: isReverse ? textAlertInterface.continueAlertText : textAlertInterface.closeAlertText,
      typeBtn: isReverse ? textAlertInterface.continueAlertTypeBtn : textAlertInterface.closeAlertTypeBtn,
      load: isReverse ? alert.load : false,
      disabled: isReverse ? textAlertInterface.actionNeeded && !selectValue : false,
      onClick: isReverse ? () => buttonContinue("save") : () => closeAlert(),
    };
    const right = {
      label: isReverse ? textAlertInterface.closeAlertText : textAlertInterface.continueAlertText,
      typeBtn: isReverse ? textAlertInterface.closeAlertTypeBtn : textAlertInterface.continueAlertTypeBtn,
      load: isReverse ? false : alert.load,
      disabled: isReverse ? false : textAlertInterface.actionNeeded && !selectValue,
      onClick: isReverse ? () => closeAlert() : () => buttonContinue("save"),
    };

    return {left, right}
  }

  const closeAlert = useCallback((caller?: string) => {
    setSelectValue('');
    setCaller(caller);
    setAlert({isVisible: false, load: false, type: false, typeContinue: ""});
    setTextAlertInterface({...textAlertInterface, positionCenter: false, reverseButtonsPosition: false})
  }, [])

  const openAlert = useCallback((type: boolean, caller?: string) => {
      setCaller(caller);
      setAlert({...alert, isVisible: true, type})
  }, [alert])

  const buttonContinue = useCallback((valor: string) => {
      setAlert({...alert, isVisible: true, load: true, typeContinue: valor})
  }, [alert])

  const resetTypeContinue = () => {
    setAlert({...alert, typeContinue: ""})
  }

  useEffect(() => {
    
  }, [alert])

  return(
    <AlertContext.Provider value={{
      setTextAlertInterface,
      openAlert,
      closeAlert,
      resetTypeContinue,
      alert,
      caller,
      selectValue,
    }}>
      {alert.isVisible ? 
        <AlertContainer>
          {textAlertInterface.positionCenter ? (
            <div className="alert-popup" id="center">
              <div className="alert-popup-mensage">
                <Title 
                  elementHTML="h3" 
                  text={textAlertInterface.title || ""} 
                  className="subtitle-small" 
                />
                <Title 
                  elementHTML="p" 
                  text={textAlertInterface.description || ""}
                  className="paragraph-small" 
                />
              </div>
              {
                textAlertInterface.selectOptions?.length ?? 0 > 0 
                ? <div style={{marginBottom: '1.5rem'}}>
                    <SelectModified
                      name="alertSelect"
                      label="Selecione o motivo"
                      options={textAlertInterface.selectOptions!}
                      value={selectValue}
                      onChange={(e) => setSelectValue(e.target.value)}
                      appearWhere="top"
                      block
                    />
                  </div>
                : null
              }
              <div className="alert-popup-btn">
                <Button
                  label={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).left.label}
                  typeBtn={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).left.typeBtn}
                  disabled={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).left.disabled}
                  load={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).left.load}
                  onClick={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).left.onClick}
                />
                
                <Button 
                  label={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).right.label}
                  typeBtn={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).right.typeBtn}
                  disabled={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).right.disabled}
                  load={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).right.load}
                  onClick={reverseObjectsButton(textAlertInterface.reverseButtonsPosition!).right.onClick}
                />
              </div>
            </div>
          ) : (
            <div className="alert-popup">
              <div className="alert-popup-mensage">
                {alert.type ? 
                  <Title elementHTML="h3" text="Salvar alterações?" className="subtitle-small" /> : 
                  <Title elementHTML="h3" text="Descartar alterações?" className="subtitle-small" />
                }
              </div>

              <div className="alert-popup-btn">
                <Button
                  label="Voltar"
                  typeBtn="outline"
                  onClick={() => closeAlert()}
                />
                {alert.type ?
                  <Button type="submit" label="Salvar" typeBtn="blue" onClick={() => buttonContinue("save")} load={alert.load} /> :
                  <Button type="button" label="Descartar" typeBtn="danger" onClick={() => buttonContinue("discard")} load={alert.load} />
                }
              </div>
            </div>
          )}
        </AlertContainer> : null
      }
      {children}
    </AlertContext.Provider>
  )
}

export const useAlert = (caller?: string) => useContext(AlertContext);